import { forwardRef } from 'react';
import styled from 'styled-components';
import { Input } from 'antd';

import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import ic_block_circle_lined from 'asserts/icons/ic_block_circle_lined.svg';
import ic_chevron_check_green from 'asserts/icons/ic_chevron_check_green.svg';
import ic_info_circle_lined from 'asserts/icons/ic_info_circle_lined.svg';

import { ReactComponent as IconEyeOff } from 'asserts/icons/ic_eye_lined_off.svg';
import { ReactComponent as IconEye } from 'asserts/icons/ic_eye_lined.svg';
import { ReactComponent as IconSearch } from 'asserts/icons/ic_search_lined_black.svg';

interface DefaultInputProps {
  name: string;
  width?: number;
  placeholder?: string;
  value?: string;
  type?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  size?: 'large' | 'middle' | 'small';
  status?: 'error' | 'warning' | 'success' | 'description';
  statusText?: string;
  disabled?: boolean;
  maxLength?: number;
  password?: boolean;
  search?: boolean;
  height?: 'large' | 'middle' | 'small';
  onSearch?: (
    value: string,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLInputElement>,
    info?: {
      source?: 'clear' | 'input';
    }
  ) => void;
}

const DefaultInput = forwardRef(
  (
    {
      name,
      width,
      placeholder,
      value,
      onChange,
      size = 'middle',
      status,
      type,
      statusText,
      disabled,
      maxLength,
      password,
      search,
      height = 'middle',
      onSearch
    }: DefaultInputProps,
    ref: any
  ) => {
    return (
      <Wrapper $width={width} $height={height}>
        {password ? (
          <Input.Password
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            maxLength={maxLength}
            allowClear
            ref={ref}
            iconRender={(visible) => (visible ? <IconEye /> : <IconEyeOff />)}
          />
        ) : search ? (
          <Input.Search
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            allowClear
            onSearch={onSearch}
            enterButton={
              <>
                <IconSearch />
              </>
            }
          />
        ) : (
          <Input
            name={name}
            type={type}
            value={value}
            placeholder={placeholder}
            allowClear
            onChange={onChange}
            size={size}
            status={status === 'success' || status === 'description' ? undefined : status}
            disabled={disabled}
            ref={ref}
            maxLength={maxLength}
          />
        )}

        {status && (
          <TextArea>
            {status === 'error' && (
              <Error>
                <img src={ic_block_circle_lined} alt="Error Icon" />
                {statusText}
              </Error>
            )}
            {status === 'success' && (
              <Success>
                <img src={ic_chevron_check_green} alt="Success Icon" />
                {statusText}
              </Success>
            )}
            {status === 'description' && (
              <Description>
                <img src={ic_info_circle_lined} alt="Description Icon" />
                {statusText}
              </Description>
            )}
          </TextArea>
        )}
      </Wrapper>
    );
  }
);

export default DefaultInput;

const Wrapper = styled.div<{ $width?: number; $height?: string }>`
  input {
    ${font.body_medium};
  }
  .ant-input-affix-wrapper {
    height: ${(props) =>
      props.$height === 'middle' ? '36px' : props.$height === 'large' ? '44px' : '32px'};
    background-color: transparent;

    .ant-input-clear-icon {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
  .ant-input-outlined {
    border-color: ${color.opacityBlack02};
  }
  .ant-input-affix-wrapper-lg {
    padding: 9px 12px;
    border-radius: 4px;
    input {
      ${font.body_medium};
      line-height: 1.5;
    }
    svg {
      width: 17.5px !important;
      height: 17.5px !important;
    }
  }
  .ant-input-disabled {
    border-color: rgba(243, 243, 243);
    input {
      cursor: not-allowed;
      color: ${color.black2};
    }
  }
  .ant-input-outlined:focus-within {
    border-color: ${color.black2} !important;
    box-shadow: none;
  }
  .ant-input-outlined:hover {
    box-shadow: 0px 0px 0px 4px ${color.opacityBlack01};
    background-color: ${color.opacityBlack01};
  }
  .ant-input-disabled {
    background-color: rgb(220, 220, 220, 0) !important;
    &:hover {
      box-shadow: 0px 0px 0px 4px rgb(220, 220, 220, 0);
      background-color: rgb(220, 220, 220, 0);
    }
  }

  .ant-input-group-wrapper.ant-input-search {
    &:hover {
      box-shadow: 0px 0px 0px 4px ${color.opacityBlack01};
      background-color: ${color.opacityBlack01};
      transition: all 0.5s;

      & .ant-input-group-addon button {
        background-color: transparent;
      }
    }

    height: ${(props) =>
      props.$height === 'middle' ? '36px' : props.$height === 'large' ? '44px' : '32px'};
    background-color: transparent;
    border: 1px solid ${color.opacityBlack02};
    border-radius: 4px;

    & .ant-input-affix-wrapper.ant-input-outlined {
      border: none;

      &:hover,
      &:focus {
        box-shadow: none;
        background-color: initial;
      }
    }

    & .ant-input-group-addon {
      background-color: transparent;

      button {
        display: flex;
        align-items: center;
        border-left: none;
        inset-inline-start: initial;
        background-color: transparent;
        transition: none;
        padding: 0 12px 0 0;
        line-height: inherit;

        span {
          height: 16px;
        }
      }
    }
  }
`;

const TextArea = styled.div`
  position: relative;
  p {
    position: absolute;
    top: 0;
    left: 0;
    margin-top: 7px;
    display: flex;
    align-items: center;
    gap: 8px;
    ${font.caption3_regular};
  }
`;

const Error = styled.p`
  color: ${color.red};
`;

const Success = styled.p`
  color: ${color.green};
`;

const Description = styled.p`
  color: ${color.azure};
`;
